import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, CommentRecord } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const AdminComments: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [comments, setComments] = useState<CommentRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');

    const fetchComments = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getComments({ page, limit: 10, search });
            setComments(response.data);
            setTotalPages(response.meta.totalPages);
        } catch (err) {
            setError(t('common.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchComments();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [page, search]);

    const handleDelete = async (id: string) => {
        if (!window.confirm(t('admin.comments.delete_confirm'))) return;
        try {
            // await adminAPI.deleteComment(id); 
            fetchComments();
        } catch (err) {
            console.error('Failed to delete comment', err);
            alert(t('common.error'));
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
                    {t('admin.comments.title')}
                </h1>
                <button
                    onClick={() => navigate('/admin')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    {t('admin.back_to_dashboard')}
                </button>
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder={t('admin.comments.search_placeholder')}
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full md:w-1/3 bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-teal-500"
                />
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">{t('admin.users.table.user')}</th>
                                <th className="px-6 py-4">{t('admin.comments.table.comment')}</th>
                                <th className="px-6 py-4">{t('admin.comments.table.rating')}</th>
                                <th className="px-6 py-4">{t('admin.comments.table.date')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        {t('admin.comments.loading')}
                                    </td>
                                </tr>
                            ) : comments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        {t('admin.comments.empty')}
                                    </td>
                                </tr>
                            ) : (
                                comments.map((comment) => (
                                    <tr key={comment.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4 font-medium text-white">
                                            {comment.user?.username || 'Unknown User'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-300 max-w-xs truncate" title={comment.content}>
                                            {comment.content}
                                        </td>
                                        <td className="px-6 py-4 text-yellow-400">
                                            {'★'.repeat(comment.rating)}
                                            <span className="text-gray-600">{'★'.repeat(5 - comment.rating)}</span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-400 text-sm">
                                            {new Date(comment.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 flex gap-2">
                                            <button
                                                onClick={() => {
                                                    // TODO: Implement delete
                                                    if (window.confirm(t('admin.comments.delete_confirm'))) {
                                                        adminAPI.deleteComment(comment.id).then(fetchComments);
                                                    }
                                                }}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-700 flex justify-between items-center">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className={`px-3 py-1 rounded ${page === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.previous')}
                        </button>
                        <span className="text-gray-400">
                            {t('common.page_info').replace('{0}', page.toString()).replace('{1}', totalPages.toString())}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className={`px-3 py-1 rounded ${page === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminComments;
